import React, { useState, useEffect, useMemo, useCallback } from "react";
import { CardDescription, Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Calendar,
  Clock,
  XCircle,
  AlertCircle,
  Upload,
  FileText,
  X,
  Send,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fetchEmployeeAssignedLeaves } from "@/services/leaveService";
import { DatePickerInput } from "../ui/DatePickerInput";
import { useActsAsEmployee } from "@/hooks/useActsAsEmployee";

// This component is the common form for both Admin (on behalf of) and Employee
const ApplyLeaveDialog = ({
  isOpen,
  setIsOpen,
  employees = [], // Only required for Admin role
  leaveEntitlement = [],
  leaveTypes = [],
  workingDaysConfig,
  holidays,
  isEmployeeView = false, // True if used by Employee, False if used by Admin
  isEmployeeOnProbation = false, // Only relevant for Employee view
  onSubmit, // Function to call on successful validation/submit
}) => {
  const [formData, setFormData] = useState({
    employee_id: "", // Used by Admin only
    leave_id: "",
    leave_name: "",
    start_date: "",
    end_date: "",
    reason: "",
    half_day: false,
    half_day_start_time: "",
    half_day_end_time: "",
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [dateValidationError, setDateValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currentAvailableBalance, setCurrentAvailableBalance] = useState(null); // --- Reset State on Dialog Open/Close ---
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState(
    leaveTypes || [],
  );
  // Employees (and admins applying their own leave from My workspace) can't
  // backdate a request.
  const actsAsEmployee = useActsAsEmployee();
  
  useEffect(() => {
    setAvailableLeaveTypes(leaveTypes || []);
  }, [leaveTypes]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        employee_id: "",
        leave_id: "",
        leave_name: "",
        start_date: "",
        end_date: "",
        reason: "",
        half_day: false,
        half_day_start_time: "",
        half_day_end_time: "",
      });
      setDocumentFile(null);
      setCalculatedDays(0);
      setDateValidationError("");
      setCurrentAvailableBalance(null);
    }
  }, [isOpen]);

  const fetchEmplLeaves = async (employeeId) => {
    try {
      const res = await fetchEmployeeAssignedLeaves(employeeId);
      setAvailableLeaveTypes(res.data.data || []);
    } catch (err) {
      console.error("fetchEmplLeaves error:", err);
      toast.error(
        err?.response?.data?.message || "Failed to load employee leaves",
      );
    }
  };
  useEffect(() => {
    if (isOpen && isEmployeeView) {
      fetchEmplLeaves();
    }
  }, [isOpen, isEmployeeView]);

  const isUnlimitedLeave = useMemo(() => {
    if (!formData.leave_id) return false;
    const leave = availableLeaveTypes.find((l) => l._id === formData.leave_id);
    console.log("leave", leave);
    return (
      (leave?.leave_type === "unpaid" && leave?.is_system_generated) || false
    );
  }, [formData.leave_id, leaveTypes]);

  const exceedBalance = useMemo(() => {
    if (isUnlimitedLeave) return false;
    if (currentAvailableBalance === null || calculatedDays === 0) return false;
    const available = currentAvailableBalance;
    return calculatedDays > available;
  }, [
    calculatedDays,
    currentAvailableBalance,
    isEmployeeView,
    isUnlimitedLeave,
    formData.leave_id,
  ]);

  const holidaySet = useMemo(() => {
    return new Set(holidays.map((h) => h.date));
  }, [holidays]);

  const isWorkingDay = useCallback(
    (checkDate) => {
      if (!workingDaysConfig) return true;

      const dateObj = new Date(checkDate);
      const dayOfWeek = dateObj.getDay();
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayName = dayNames[dayOfWeek];

      const dateStr = format(dateObj, "yyyy-MM-dd");
      if (holidaySet.has(dateStr)) return false;

      let scheduleConfig = null;

      if (isEmployeeView) {
        scheduleConfig = workingDaysConfig?.schedule || null;
      } else {
        const empConfig = workingDaysConfig[formData.employee_id];
        if (empConfig) scheduleConfig = empConfig.schedule;
      }

      if (!scheduleConfig) return true;

      const dayConfig = scheduleConfig[dayName];
      if (!dayConfig || !dayConfig.isWorking) return false;

      if (
        Array.isArray(dayConfig.specificWeeks) &&
        dayConfig.specificWeeks.length > 0
      ) {
        const weekNo = Math.ceil(dateObj.getDate() / 7);
        return dayConfig.specificWeeks.includes(weekNo);
      }

      return true;
    },
    [workingDaysConfig, isEmployeeView, formData.employee_id, holidaySet],
  );

  useEffect(() => {
    const { start_date, end_date, half_day } = formData;

    if (!isValidISODate(start_date) || !isValidISODate(end_date)) {
      setCalculatedDays(0);
      return;
    }

    if (half_day) {
      setCalculatedDays(0.5);
      return;
    }

    const days = calculateWorkingDays(start_date, end_date);
    setCalculatedDays(days);
  }, [formData.start_date, formData.end_date, formData.half_day]);

  const isValidISODate = (value) => {
    if (typeof value !== "string") return false;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const isValidTimeString = (value) =>
    typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

  const halfDayTimeToMinutes = (value) => {
    const [hours, minutes] = value.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const validateHalfDayTimes = (form) => {
    if (!form.half_day) return "";

    if (!form.half_day_start_time || !form.half_day_end_time) {
      return "Enter the start and end time you will work on the half day.";
    }

    if (
      !isValidTimeString(form.half_day_start_time) ||
      !isValidTimeString(form.half_day_end_time)
    ) {
      return "Half-day times must be in HH:mm 24-hour format.";
    }

    if (
      halfDayTimeToMinutes(form.half_day_end_time) <=
      halfDayTimeToMinutes(form.half_day_start_time)
    ) {
      return "Half-day end time must be after the start time.";
    }

    return "";
  };

  const getHolidayName = (dateStr) => {
    const holiday = holidays.find((h) => h.date === dateStr);
    return holiday ? holiday.name : "";
  };

  const calculateWorkingDays = useCallback(
    (startDate, endDate) => {
      if (!startDate || !endDate) return 0;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) return 0;

      let count = 0;
      let current = new Date(start);

      while (current <= end) {
        if (isWorkingDay(current)) count++;
        current.setDate(current.getDate() + 1);
      }
      return count;
    },
    [isWorkingDay],
  );

  const validateLeaveDates = (startDate, endDate) => {
    if (!startDate) return "";

    const start = new Date(startDate);
    const formattedDate = format(start, "dd MMM yyyy");
    const dateStr = format(start, "yyyy-MM-dd");

    // Check holiday first
    const holidayName = getHolidayName(dateStr);
    if (holidayName) {
      return `Start date (${formattedDate}) falls on a holiday: ${holidayName}.`;
    }

    // Check working day through schedule
    if (!isWorkingDay(start)) {
      /**
       * If coming here means:
       * → Day exists in schedule
       * → But schedule.isWorking = false
       */
      let scheduleConfig = null;

      if (isEmployeeView) {
        scheduleConfig = workingDaysConfig?.schedule || null;
      } else {
        const empCfg = workingDaysConfig[formData.employee_id];
        scheduleConfig = empCfg?.schedule || null;
      }

      if (!scheduleConfig) {
        return ""; // no validation possible
      }

      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayName = dayNames[start.getDay()];
      const dayConfig = scheduleConfig[dayName];

      // Means schedule explicitly marks non-working
      if (dayConfig && dayConfig.isWorking === false) {
        return `Start date (${formattedDate}) is a non-working ${dayName}.`;
      }

      // Handle specificWeek logic when defined
      if (
        dayConfig &&
        Array.isArray(dayConfig.specificWeeks) &&
        dayConfig.specificWeeks.length > 0
      ) {
        const weekOfMonth = Math.ceil(start.getDate() / 7);
        return `Start date (${formattedDate}) is not allowed for this ${dayName} (valid weeks: ${dayConfig.specificWeeks.join(
          ", ",
        )})`;
      }
    }

    return "";
  };

  const updateBalance = (employeeId, leaveId) => {
    if (!leaveId || !employeeId) {
      setCurrentAvailableBalance(null);
      return;
    }

    let entitlementsArray;
    let targetEntitlement = null;

    if (isEmployeeView) {
      entitlementsArray = leaveEntitlement;
    } else {
      if (leaveEntitlement && leaveEntitlement[employeeId]) {
        entitlementsArray = leaveEntitlement[employeeId];
      } else {
        setCurrentAvailableBalance(0);
        return;
      }
    }

    if (entitlementsArray) {
      targetEntitlement = entitlementsArray.find(
        (ent) => ent.leave_id === leaveId,
      );
    }

    let available = targetEntitlement
      ? targetEntitlement.total_allocated - (targetEntitlement.used || 0)
      : 0; // Default to 0 if the specific leave type isn't found for the user/employee

    // 4. Apply probation override (Only relevant in Employee view and if the flag is true)
    // if (isEmployeeView && isEmployeeOnProbation) {
    //   available = 0;
    // }

    setCurrentAvailableBalance(available);
  };

  const handleSelectChange = (field, value) => {
    let updatedForm = { ...formData, [field]: value };

    // Logic specific to employee selection (Admin view)
    if (field === "employee_id") {
      // Clear dependent fields/balance when employee changes
      updatedForm = { ...updatedForm, leave_id: "", leave_name: "" };
      updateBalance(value, "");
      fetchEmplLeaves(value);
    }

    // Logic specific to leave type selection
    if (field === "leave_name") {
      const parsed = JSON.parse(value);
      updatedForm = {
        ...updatedForm,
        leave_id: parsed.id,
        leave_name: parsed.name,
      };
      const employeeId = isEmployeeView ? "SELF" : formData.employee_id;
      updateBalance(employeeId, parsed.id);
    }

    setFormData(updatedForm);
  };

  const handleDateChange = (field, value) => {
    // ⛔ Do nothing if date is incomplete / invalid
    if (
      (field === "start_date" || field === "end_date") &&
      !isValidISODate(value)
    ) {
      return;
    }

    // Validation only when date is valid
    const error = validateLeaveDates(
      field === "start_date" ? value : formData.start_date,
      field === "end_date" ? value : formData.end_date,
    );

    // if (formData.half_day && formData.start_date !== formData.end_date) {
    //   setDateValidationError("Half-day can only be taken for one day.");
    //   return;
    // }

    // setDateValidationError(error || "");

        setFormData((prev) => {
          const next = {
            ...prev,
            [field]: value,
          };

          // 🔐 Half-day validation — ALWAYS use `next`
          if (
            next.half_day &&
            next.start_date &&
            next.end_date &&
            next.start_date !== next.end_date
          ) {
            setDateValidationError("Half-day can only be taken for one day.");
          } else {
            const halfDayTimeError = validateHalfDayTimes(next);

            if (halfDayTimeError) {
              setDateValidationError(halfDayTimeError);
            } else {
              const error = validateLeaveDates(next.start_date, next.end_date);
              setDateValidationError(error || "");
            }
          }

          return next;
        });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type))
      return toast.error("Only PDF, JPG, PNG allowed");

    if (file.size > 5 * 1024 * 1024)
      return toast.error("File too large (max 5MB)");

    setDocumentFile(file);
  };

  const handleFinalSubmit = async () => {
    if (
      (!isEmployeeView && !formData.employee_id) ||
      !formData.leave_id ||
      !formData.start_date ||
      !formData.end_date ||
      !formData.reason.trim()
    ) {
      return toast.error("Please fill all required fields");
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    if (isEmployeeView) {
      if (start < today) {
        return toast.error("Start date cannot be before today.");
      }

      if (end < today) {
        return toast.error("End date cannot be before today.");
      }
    }

    if (end < start) {
      return toast.error("End date cannot be before start date.");
    }

    if (dateValidationError) return toast.error(dateValidationError);
    if (calculatedDays === 0)
      return toast.error("Selected range contains no working days.");
    if (exceedBalance)
      return toast.error(
        `Requested ${calculatedDays} days exceeds available ${currentAvailableBalance} days.`,
      );

    setSubmitting(true);
    try {
      await onSubmit(formData, calculatedDays, documentFile);
      setIsOpen(false); // Close dialog on success
    } catch (error) {
      // Error handling assumed to be done inside onSubmit, but fail-safe here
      toast.error(error.message || "Failed to submit leave request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Trigger is handled by the parent component */}
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {isEmployeeView ? "Apply for Leave" : "Apply Leave for Employee"}
          </DialogTitle>
          <CardDescription>
            Fill out the details below to submit the leave request.
          </CardDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* ADMIN VIEW: EMPLOYEE SELECTOR */}
          {!isEmployeeView && (
            <div>
              <Label className="text-sm font-medium">
                Employee <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.employee_id}
                onValueChange={(v) => handleSelectChange("employee_id", v)}
                disabled={!employees || employees.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {employees.map((emp) => (
                    <SelectItem key={emp._id} value={emp._id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* LEAVE TYPE SELECTOR (Shared) */}
          <div>
            <Label className="text-sm font-medium">
              Leave Type <span className="text-red-500">*</span>
            </Label>
            <Select
              value={
                formData.leave_id
                  ? JSON.stringify({
                      id: formData.leave_id,
                      name: formData.leave_name,
                    })
                  : ""
              }
              onValueChange={(v) => handleSelectChange("leave_name", v)}
              disabled={
                !availableLeaveTypes.length ||
                (!isEmployeeView && !formData.employee_id)
              }
            >
              <SelectTrigger className="text-gray-900 dark:text-gray-200">
                <SelectValue placeholder="Select leave type" />
              </SelectTrigger>
              <SelectContent>
                {availableLeaveTypes.map((leave) => (
                  <SelectItem
                    key={leave._id || leave.leave_id}
                    value={JSON.stringify({
                      id: leave._id || leave.leave_id,
                      name: leave.leave_name,
                    })}
                  >
                    {leave.leave_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* BALANCE DISPLAY (Shared) */}
          {currentAvailableBalance !== null && !isUnlimitedLeave && (
            <div
              className={`mt-2 p-3 border rounded-lg flex items-center justify-between transition-colors ${
                exceedBalance
                  ? "bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700"
                  : "bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700"
              }`}
            >
              <span
                className={`text-sm font-medium ${
                  exceedBalance
                    ? "text-red-800 dark:text-red-300"
                    : "text-blue-800 dark:text-blue-300"
                }`}
              >
                {exceedBalance
                  ? "Requested Days Exceed Balance:"
                  : "Available Balance:"}
              </span>

              <span
                className={`text-lg font-bold ${
                  exceedBalance
                    ? "text-red-700 dark:text-red-400"
                    : "text-blue-700 dark:text-blue-400"
                }`}
              >
                {isUnlimitedLeave ? (
                  <span className="">Unlimited</span>
                ) : (
                  <>
                    {currentAvailableBalance} day
                    {currentAvailableBalance !== 1 ? "s" : ""}
                  </>
                )}
              </span>
            </div>
          )}

          {/* DATES + HALF DAY (Shared) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label className="text-sm font-medium">
                Start Date <span className="text-red-500">*</span>
              </Label>
              <DatePickerInput
                value={formData.start_date}
                minDate={actsAsEmployee ? new Date() : null}
                onChange={(value) => handleDateChange("start_date", value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">
                End Date <span className="text-red-500">*</span>
              </Label>
              <DatePickerInput
                value={formData.end_date}
                minDate={
                  formData.start_date
                    ? new Date(formData.start_date)
                    : new Date()
                }
                onChange={(value) => handleDateChange("end_date", value)}
              />
            </div>
            <div className="flex items-center space-x-2 h-10">
              <Checkbox
                id="half_day"
                checked={formData.half_day}
                onCheckedChange={(checked) =>
                  handleDateChange("half_day", Boolean(checked))
                }
              />
              <Label htmlFor="half_day" className="text-sm font-medium">
                Half Day
              </Label>
            </div>
          </div>

          {formData.half_day && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">
                  Start Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="time"
                  value={formData.half_day_start_time}
                  onChange={(e) =>
                    handleDateChange("half_day_start_time", e.target.value)
                  }
                />
              </div>
              <div>
                <Label className="text-sm font-medium">
                  End Time <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="time"
                  value={formData.half_day_end_time}
                  onChange={(e) =>
                    handleDateChange("half_day_end_time", e.target.value)
                  }
                />
              </div>
            </div>
          )}

          {/* VALIDATION/CALCULATION FEEDBACK (Shared) */}
          {dateValidationError && (
            <div className="bg-destructive/5 border border-destructive dark:border-destructive p-3 rounded-md text-sm">
              <div className="flex items-start space-x-2">
                <XCircle className="h-5 w-5 text-destructive dark:text-destructive" />
                <p className="text-destructive dark:text-destructive">
                  {dateValidationError}
                </p>
              </div>
            </div>
          )}

          {formData.start_date && formData.end_date && !dateValidationError && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  Calculated Days:
                </span>
              </div>
              <span className="text-xl font-extrabold text-blue-700 dark:text-blue-400">
                {calculatedDays} {calculatedDays === 1 ? "day" : "days"}
              </span>
            </div>
          )}

          {/* REASON (Shared) */}
          <div>
            <Label className="text-sm font-medium">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              rows={4}
              value={formData.reason}
              placeholder="e.g., Annual vacation, mild fever, family emergency..."
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reason: e.target.value }))
              }
              required
            />
          </div>

          {/* DOCUMENT UPLOAD (Shared) */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Attach Document (Optional)
            </Label>
            {!documentFile ? (
              <label
                htmlFor="apply-leave-document-upload"
                className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:border-blue-400"
              >
                <Upload className="h-7 w-7 text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  PDF, JPG, PNG (Max 5MB)
                </p>
                <input
                  id="apply-leave-document-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
            ) : (
              <div className="border border-green-300 dark:border-green-700 rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2 text-sm text-green-800 dark:text-green-300">
                    <FileText className="h-5 w-5" />
                    <span className="truncate max-w-xs">
                      {documentFile.name}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDocumentFile(null)}
                    className="text-red-600 hover:bg-red-100 dark:hover:bg-red-900"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-4 gap-4 sm:gap-2 flex-col sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>

          <Button
            onClick={handleFinalSubmit}
            disabled={
              submitting ||
              !!dateValidationError ||
              calculatedDays === 0 ||
              exceedBalance ||
              !formData.leave_id ||
              (!isEmployeeView && !formData.employee_id)
            }
            className="w-full sm:w-auto flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <Clock className="h-4 w-4 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Submit Request</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApplyLeaveDialog;
